import { defaultsDeep, isPlainObject } from 'lodash';
import Debug from 'debug';
import getConfig from 'next/config';

import { isJwtWithinExpiryTime } from 'lib/utils/checkJwtTokenValidity';
import { camelCase, snakeCase } from 'lib/utils/objectSerializer';
import redirect from 'lib/utils/redirect';
import { isServer } from 'lib/common/constants';

import fetch from './fetch';

const {
  publicRuntimeConfig: {
    enableSeparateWebRomAuth,
    restaurantHubInternalAPIURL,
    restaurantHubDataAPIURL,
    restaurantHubDataEndpoints,
  },
} = getConfig();

const debug = Debug('rs:store/middleware/api');

const defaultOptions = {
  method: 'GET',
  credentials: 'include',
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
};

class AccessTokenRefreshError extends Error {
  constructor(message, response) {
    super(message);
    this.response = response;
  }
}

export class ApiMiddlewareError extends Error {
  constructor(message, action) {
    super(message);
    this.name = 'ApiMiddlewareError';
    this.action = action;
  }
}

const isDataAPIEndpoint = (url) => {
  if (url.startsWith('/api/')) {
    return (
      restaurantHubDataEndpoints &&
      restaurantHubDataEndpoints.find((e) => url.indexOf(e) > -1)
    );
  }
  return false;
};
/**
 * Formats the URL for the API.
 *
 * On the server-side the `origin` portion is required as
 * the server doesn't have a request context like a
 * browser does.
 *
 * Also rewrites api endpoint calls for non-critical,
 * data related endpoints (e.g. reviews, ratings, etc)
 * to be routed to the restaurant hub data api endpoint
 * @param {Object} params
 * @param {String} params.origin
 * @param {String} params.path
 */
const formatApiUrl = ({ path, params = {} }) => {
  let queryString;
  let pathname;
  if (path.includes('?')) {
    const queryStart = path.indexOf('?');
    queryString = path.slice(queryStart + 1);
    pathname = path.slice(0, queryStart);
  } else {
    pathname = path;
  }

  const queryParams = new URLSearchParams(queryString); // attempt to percent decode
  Object.entries(params).forEach(([k, v]) => queryParams.set(k, v)); // percent encode
  queryString = queryParams.toString();

  if (queryString) {
    queryString = `?${queryString}`;
  }

  // check if the desired API endpoint needs to be rerouted to the rs data api endpoint
  if (isDataAPIEndpoint(pathname)) {
    return `${restaurantHubDataAPIURL}${pathname}${queryString}`;
  }

  return isServer
    ? `${restaurantHubInternalAPIURL}${pathname}${queryString}`
    : `${pathname}${queryString}`;
};

/**
 *  Makes sure the sames requests, defined by having the same URL and method,
 *  are not made twice, but are still made with the same order as the pending
 *  queue.
 */
const dedupAPICallQueue = (queue) => {
  const dedupedAPICallQueue = [];
  const dedupedAPICallQueueIndexMap = new Map();
  queue.forEach(({ url: requestUrl, method, request }) => {
    const key = `${requestUrl}_${method}`;
    if (dedupedAPICallQueueIndexMap.has(key)) {
      dedupedAPICallQueue[dedupedAPICallQueueIndexMap.get(key)] = request;
    } else {
      dedupedAPICallQueue.push(request);
      dedupedAPICallQueueIndexMap.set(key, dedupedAPICallQueue.length - 1);
    }
  });

  debug('dedupedAPICallQueue: %o', dedupedAPICallQueue);
  debug('dedupedAPICallQueueIndexMap: %o', dedupedAPICallQueueIndexMap);

  return dedupedAPICallQueue;
};

// If unauthorized, it can be one of the following cases
// 1. no access token -> redirect to login
// 2. access token expired but refresh token hasn't -> retry once on the server
// 3. both access token and refresh token has expired -> redirect to login
const checkAuth =
  (
    url,
    fetchOptions,
    dispatch,
    action,
    acquireAccessTokenRefreshLock,
    releaseAccessTokenRefreshLock,
    isAccessTokenRefreshLocked,
    getPendingAPICallQueue,
    resetPendingAPICallQueue,
    addToPendingQueue,
    getAccessToken,
  ) =>
  async (response) => {
    debug('checking auth for %s', url);
    if (response.status === 403 || response.status === 401) {
      // In the case of the user logging in, we don't want to redirect to the login when we
      // get a 401 page again since that means we lose the error state. In all other cases
      // however, we do want to redirect to login since it means the user is trying to do
      // something they are not allowed to do.

      if (action.type === 'LOGIN') {
        debug('login request, passing thru checkAuth...');
        return response;
      }

      if (enableSeparateWebRomAuth) {
        const webRomHostFinder = /rs-client-api[^.]*\./;
        if (webRomHostFinder.test(url)) {
          dispatch({
            type: 'AUTHENTICATE_LIVE_ORDERS',
            extra: {
              branchId: action.extra?.branchId,
            },
          });
          return response;
        }
      }

      const token = getAccessToken();
      if (isJwtWithinExpiryTime(token)) {
        return response;
      }

      debug('refreshing token...');
      if (isAccessTokenRefreshLocked()) {
        addToPendingQueue(url, fetchOptions);
        return undefined;
      }

      acquireAccessTokenRefreshLock();

      const authResponse = await fetch(
        formatApiUrl({
          path: '/api/session/refresh',
        }),
        {
          method: 'POST',
          headers: fetchOptions.headers,
        },
      );

      if (authResponse.status >= 400) {
        debug('token refresh failure, redirecting...');
        throw new AccessTokenRefreshError(
          authResponse.statusText,
          authResponse,
        );
      }

      const authPayload = await authResponse.json();

      debug('token refreshed, retrying...');

      dispatch({
        type: 'UPDATE_ACCESS_TOKEN',
        payload: authPayload,
      });

      releaseAccessTokenRefreshLock();

      const pendingAPICallQueue = getPendingAPICallQueue();
      try {
        if (pendingAPICallQueue.length) {
          debug('executing pending API calls...');
          const dedupedAPICallQueue = dedupAPICallQueue(pendingAPICallQueue);
          await Promise.all(dedupedAPICallQueue.map((request) => request()));
        }
      } finally {
        resetPendingAPICallQueue();
      }

      return fetch(url, {
        ...fetchOptions,
        headers: {
          ...fetchOptions.headers,
          Authorization: `Bearer ${authPayload.access_token}`,
        },
      });
    }

    debug("auth'ed: %s", url);

    return response;
  };

const toResponseActionPayload = async (response) => {
  const { status, headers } = response;

  if (status === 204) return { status, headers, payload: undefined };

  if (
    (response.headers.get('Content-Type') || '').includes('application/json')
  ) {
    try {
      const payload = await response.json();
      return {
        status,
        headers,
        payload,
      };
    } catch (error) {
      debug('Error deserializing JSON body: %o', error);
      return {
        status: 0,
        headers: undefined,
        payload: undefined,
      };
    }
  }

  try {
    const payload = await response.text();
    return {
      status,
      headers,
      payload,
    };
  } catch (error) {
    debug('Error deserializing text body: %o', error);
    return {
      status: 0,
      headers: undefined,
      payload: undefined,
    };
  }
};

const getResponseActionDispatcher =
  (dispatch, action) => async (responseActionPayload) => {
    const { type: actionType } = action;
    const { status } = responseActionPayload;
    const ok = status >= 200 && status < 300;
    const type = ok ? `${actionType}_SUCCESS` : `${actionType}_FAILURE`;

    const responseAction = {
      type,
      extra: action.extra || {},
      ...responseActionPayload,
    };

    if (responseAction?.payload && action.extra?.convertCase)
      responseAction.payload = camelCase(responseAction.payload);

    debug('dispatching action: %o', responseAction);

    dispatch(responseAction);

    if (ok) return responseAction;

    if (action.silent_exception) return {};

    throw new ApiMiddlewareError(responseAction.type, responseAction);
  };

// FIXME:
// Refactor this POS so all rejections are actual JS Error objects.
// FIXME:
// The FAILURE action should be dispatched after any error instead of only
// after a successful body parse.
const makeRequest = async (
  url,
  options,
  dispatch,
  action,
  getAccessToken,
  acquireAccessTokenRefreshLock,
  releaseAccessTokenRefreshLock,
  isAccessTokenRefreshLocked,
  getPendingAPICallQueue,
  resetPendingAPICallQueue,
  addToPendingQueue,
) => {
  const fetchOptions = { ...options };

  // add the session token to the request
  const token = getAccessToken();
  if (token) {
    fetchOptions.headers.Authorization = `Bearer ${token}`;
  }

  debug(`${fetchOptions.method} ${url}`);

  let response = await fetch(url, fetchOptions);

  debug(`${fetchOptions.method} ${url} responded`);

  const authCheck = checkAuth(
    url,
    fetchOptions,
    dispatch,
    action,
    acquireAccessTokenRefreshLock,
    releaseAccessTokenRefreshLock,
    isAccessTokenRefreshLocked,
    getPendingAPICallQueue,
    resetPendingAPICallQueue,
    addToPendingQueue,
    getAccessToken,
  );

  try {
    response = await authCheck(response);
  } catch (error) {
    // FIXME: there should not be any difference between client and server API requests
    if (error instanceof AccessTokenRefreshError && !isServer) {
      // FIXME: deal with redirection in an error boundary component after refactoring
      redirect({ url: '/login' });
      // eslint-disable-next-line no-throw-literal
      throw undefined;
    }
  }

  // It's a really cringy hack, but that's how we avoid triggering actions
  // about 401s that need to be retried with the new token (if we get 401 WHILE refreshing the token)
  // We also prevent sending errors to sentry returning undefined instead of creating a response action.
  // See {@link getResponseActionDispatcher}
  // We don't want to signal it because, if we get 401 while refreshing the token
  // it will be added to the queue and retried.
  if (typeof response === 'undefined') {
    return undefined;
  }

  debug('auth status: %s', response.status);

  const responseActionPayload = await toResponseActionPayload(response);

  const responseActionDispatcher = getResponseActionDispatcher(
    dispatch,
    action,
  );

  return responseActionDispatcher(responseActionPayload);
};

/**
 * Middleware for making requests to the restaurant-hub-api.
 *
 * Dispatch an action like:
 *
 * {
 *   type: 'LOGIN',
 *   endpoint: '/login', // <<- The hook that invokes this middleware
 *   options: {
 *     ...fetchOptions,
 *   },
 *   extra: {
 *     // pass any additional data to the reducer
 *   },
 * }
 *
 * @param store
 */
export default ({ dispatch, getState }) => {
  let isRefreshingAccessToken = false;

  let pendingAPICallQueue = [];

  const acquireAccessTokenRefreshLock = () => {
    debug('acquiring access token refresh lock');
    isRefreshingAccessToken = true;
    return isRefreshingAccessToken;
  };

  const releaseAccessTokenRefreshLock = () => {
    debug('releasing access token refresh lock');
    isRefreshingAccessToken = false;
    return isRefreshingAccessToken;
  };

  const isAccessTokenRefreshLocked = () => {
    return isRefreshingAccessToken;
  };

  const getPendingAPICallQueue = () => pendingAPICallQueue;

  const resetPendingAPICallQueue = () => {
    pendingAPICallQueue = [];
    debug('pending api call queue cleared');
  };

  const getAccessToken = () => getState().request.token;

  return (next) =>
    (action = {}) => {
      if (Object.keys(action).includes('endpoint') && !action.endpoint) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({ status: 0 });
      }

      const addToPendingQueue = (reqUrl, reqOptions) => {
        return new Promise((resolve, reject) => {
          const delayedRequest = () =>
            makeRequest(
              reqUrl,
              reqOptions,
              dispatch,
              action,
              getAccessToken,
              acquireAccessTokenRefreshLock,
              releaseAccessTokenRefreshLock,
              isAccessTokenRefreshLocked,
              getPendingAPICallQueue,
              resetPendingAPICallQueue,
              addToPendingQueue,
            )
              .then(resolve)
              .catch(reject);

          pendingAPICallQueue.push({
            reqUrl,
            method: reqOptions.method,
            request: delayedRequest,
          });
        });
      };

      const { endpoint, params } = action;

      if (!endpoint) {
        return next(action);
      }

      dispatch({
        type: `${action.type}_REQUEST`,
        extra: action.extra || {},
      });

      const { request } = getState();
      const { host, ssoOrgId, userAgent } = request;
      const userAgentEnv = isServer ? 'server' : 'browser';

      if (!host && isServer) {
        throw new TypeError(
          'Server-side fetch missing `request.host` in state',
        );
      }

      const url = formatApiUrl({ path: endpoint, params });
      const options = defaultsDeep({}, action.options, defaultOptions);

      // adds meaningful useragent for apis
      options.headers[
        'User-Agent'
      ] = `${userAgent} (deliveroo/restaurant-home ${userAgentEnv})`;

      // add the organization context for internal sso users
      if (ssoOrgId && !url.includes('rs-client-api')) {
        options.headers['X-Roo-Org-Id'] = ssoOrgId;
      }

      if (typeof FormData !== 'undefined' && options.body instanceof FormData) {
        // eslint-disable-next-line no-unused-vars
        const { 'Content-Type': _, ...providedHeaders } = options.headers;
        options.headers = providedHeaders;
      }

      if (
        options.headers['Content-Type'] === 'application/json' &&
        options.body
      ) {
        if (
          action.extra?.convertCase &&
          (isPlainObject(options.body) || Array.isArray(options.body))
        )
          options.body = snakeCase(options.body);

        options.body = JSON.stringify(options.body);
      }

      if (isRefreshingAccessToken) {
        debug(
          `access token refresh in progress, queueing request for ${url}...`,
        );
        return addToPendingQueue(url, options);
      }

      return makeRequest(
        url,
        options,
        dispatch,
        action,
        getAccessToken,
        acquireAccessTokenRefreshLock,
        releaseAccessTokenRefreshLock,
        isAccessTokenRefreshLocked,
        getPendingAPICallQueue,
        resetPendingAPICallQueue,
        addToPendingQueue,
      );
    };
};

export { defaultOptions };
